/**
 * token-storage.ts — Nơi cất access token + refresh token của CRM.
 *
 * "Duy trì đăng nhập" (bật, mặc định) → localStorage: còn nguyên sau khi đóng trình duyệt, dùng chung mọi tab.
 * Tắt → sessionStorage: chỉ sống trong tab này, đóng trình duyệt/tab là mất.
 *
 * Quy tắc:
 *  - Đọc: token nằm ở đâu thì refresh token đọc ở đó (ưu tiên sessionStorage nếu tab này đăng nhập "không duy trì").
 *  - `setTokens(token, refresh)` KHÔNG kèm `remember` (làm mới token, xem hộ nhân viên…) → ghi vào đúng chỗ đang dùng,
 *    nên luồng refresh không vô tình đổi phiên "tạm" thành phiên "duy trì".
 *  - Kèm `remember` (đăng nhập / đăng ký) → ghi vào nơi tương ứng và xoá sạch nơi còn lại.
 *  - Trình duyệt chặn lưu trữ (chế độ riêng tư…) → không ném lỗi.
 */
import { REFRESH_KEY, TOKEN_KEY } from './config'

export interface KeyValueStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

export type TokenLocation = 'local' | 'session'

export interface TokenStorage {
  getToken(): string | null
  getRefreshToken(): string | null
  setTokens(token: string, refreshToken?: string, remember?: boolean): void
  clearTokens(): void
  clearRefreshToken(): void
  /** Nơi đang giữ phiên (mặc định `local` khi chưa có token). */
  location(): TokenLocation
}

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn()
  } catch {
    return fallback
  }
}

export function createTokenStorage(opts: {
  local: () => KeyValueStore | null | undefined
  session: () => KeyValueStore | null | undefined
  tokenKey?: string
  refreshKey?: string
}): TokenStorage {
  const tokenKey = opts.tokenKey ?? TOKEN_KEY
  const refreshKey = opts.refreshKey ?? REFRESH_KEY
  const store = (where: TokenLocation) => safe(() => (where === 'local' ? opts.local() : opts.session()) ?? null, null)
  const get = (where: TokenLocation, k: string) => safe(() => store(where)?.getItem(k) ?? null, null)
  const put = (where: TokenLocation, k: string, v: string) => safe(() => store(where)?.setItem(k, v), undefined)
  const del = (where: TokenLocation, k: string) => safe(() => store(where)?.removeItem(k), undefined)

  const location = (): TokenLocation => (get('session', tokenKey) ? 'session' : 'local')

  return {
    location,
    getToken: () => get(location(), tokenKey),
    getRefreshToken: () => get(location(), refreshKey),
    setTokens(token, refreshToken, remember) {
      const current = location()
      const target: TokenLocation = remember === undefined ? current : remember ? 'local' : 'session'
      if (target !== current) {
        // Chuyển nơi lưu: mang refresh token cũ theo nếu lần này không có refresh mới.
        const carried = refreshToken ? null : get(current, refreshKey)
        del(current, tokenKey)
        del(current, refreshKey)
        if (carried) put(target, refreshKey, carried)
      }
      if (remember !== undefined) {
        const other: TokenLocation = target === 'local' ? 'session' : 'local'
        del(other, tokenKey)
        del(other, refreshKey)
      }
      put(target, tokenKey, token)
      if (refreshToken) put(target, refreshKey, refreshToken)
    },
    clearTokens() {
      for (const w of ['local', 'session'] as const) {
        del(w, tokenKey)
        del(w, refreshKey)
      }
    },
    clearRefreshToken() {
      del('local', refreshKey)
      del('session', refreshKey)
    },
  }
}

/** Kho token dùng chung của CRM (trình duyệt thật). */
export const tokenStorage = createTokenStorage({
  local: () => (typeof window === 'undefined' ? null : window.localStorage),
  session: () => (typeof window === 'undefined' ? null : window.sessionStorage),
})
